import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Terminal, ArrowRight, ArrowLeft, Zap, Code, BarChart2, Users, BookOpen, Copy, Check, ChevronRight } from 'lucide-react';
import { useAuth, API } from '../App';

const ROLES = [
  { id: 'developer', label: 'Developer', icon: Code, desc: 'Ship better code with AI committee review', playbook: 'code-review' },
  { id: 'investor', label: 'Investor', icon: BarChart2, desc: 'Evaluate deals with multi-perspective analysis', playbook: 'investment-analysis' },
  { id: 'researcher', label: 'Researcher', icon: BookOpen, desc: 'Stress-test hypotheses through structured debate', playbook: 'research-review' },
  { id: 'team-lead', label: 'Team Lead', icon: Users, desc: 'Make better decisions with AI committee input', playbook: 'strategy-review' },
];

const MCP_PLATFORMS = [
  { id: 'claude-code', label: 'Claude Code', cmd: (key) => `claude mcp add fray --transport sse --url https://www.getfray.ai/mcp --header "Authorization: Bearer ${key || 'YOUR_KEY'}"` },
  { id: 'claude-desktop', label: 'Claude Desktop', cmd: (key) => JSON.stringify({ mcpServers: { fray: { type: "url", url: "https://www.getfray.ai/mcp", headers: { Authorization: `Bearer ${key || 'YOUR_KEY'}` } } } }, null, 2) },
  { id: 'cursor', label: 'Cursor', cmd: (key) => JSON.stringify({ mcpServers: { fray: { url: "https://www.getfray.ai/mcp", headers: { Authorization: `Bearer ${key || 'YOUR_KEY'}` } } } }, null, 2) },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState(null);
  const [platform, setPlatform] = useState('claude-code');
  const [copied, setCopied] = useState('');
  const [apiKey, setApiKey] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (user?.onboarding_completed) navigate('/dashboard');
  }, [user, navigate]);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const generateKey = async () => {
    setGenerating(true);
    try {
      const res = await API.post('/api/user/mcp-key/regenerate');
      setApiKey(res.data.key);
    } catch { /* ignore */ }
    setGenerating(false);
  };

  const completeOnboarding = async () => {
    try {
      await API.post('/api/auth/complete-onboarding', { role, skipped_mcp: step === 2 });
      await refreshUser();
    } catch { /* ignore */ }
    navigate('/dashboard');
  };

  const launchFirstFray = () => {
    const playbook = ROLES.find(r => r.id === role)?.playbook || 'strategy-review';
    completeOnboarding();
    navigate(`/playbooks/${playbook}`);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F1E8] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 flex items-center justify-center text-xs font-mono border ${
                s === step ? 'bg-[#FF4D00] border-[#FF4D00] text-[#0A0A0A] font-bold'
                : s < step ? 'bg-[#22C55E]/20 border-[#22C55E]/40 text-[#22C55E]'
                : 'border-[#2A2A2A] text-[#A0A0A0]'
              }`}>{s < step ? '✓' : s}</div>
              {s < 3 && <div className={`w-8 h-px ${s < step ? 'bg-[#22C55E]/40' : 'bg-[#2A2A2A]'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Welcome/Role */}
        {step === 1 && (
          <div data-testid="onboarding-step-1">
            <div className="text-center mb-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
                <Zap className="w-6 h-6 text-[#0A0A0A]" />
              </div>
              <h1 className="text-2xl font-bold font-display uppercase tracking-wider mb-2">Welcome to Fray</h1>
              <p className="text-sm text-[#A0A0A0]">What brings you here? This helps us personalize your first experience.</p>
            </div>
            <div className="space-y-2 mb-6">
              {ROLES.map(r => {
                const Icon = r.icon;
                return (
                  <button key={r.id} onClick={() => setRole(r.id)}
                    className={`w-full p-4 min-h-[44px] border text-left flex items-center gap-3 transition-colors ${
                      role === r.id ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#3A3A3A]'
                    }`}
                    data-testid={`role-${r.id}`}>
                    <Icon className={`w-5 h-5 shrink-0 ${role === r.id ? 'text-[#FF4D00]' : 'text-[#A0A0A0]'}`} />
                    <div>
                      <span className={`text-sm font-semibold ${role === r.id ? 'text-[#F5F1E8]' : 'text-[#A0A0A0]'}`}>{r.label}</span>
                      <p className="text-[10px] text-[#A0A0A0]">{r.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setStep(2)} disabled={!role}
              className="w-full py-3 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] font-bold font-display uppercase tracking-wider disabled:opacity-30 hover:bg-[#FF6B33] transition-colors flex items-center justify-center gap-2"
              data-testid="onboarding-next-1">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 2: MCP Connect */}
        {step === 2 && (
          <div data-testid="onboarding-step-2">
            <div className="text-center mb-6">
              <Terminal className="w-8 h-8 mx-auto mb-3 text-[#00D4FF]" />
              <h2 className="text-xl font-bold font-display uppercase tracking-wider mb-2">Connect Your AI Tools</h2>
              <p className="text-sm text-[#A0A0A0]">Add Fray to your favorite AI tool via MCP. Takes 30 seconds.</p>
            </div>

            {/* Generate API key */}
            {!apiKey && (
              <button onClick={generateKey} disabled={generating}
                className="w-full py-3 min-h-[44px] mb-4 border border-[#00D4FF]/30 bg-[#00D4FF]/5 text-[#00D4FF] font-mono text-sm hover:bg-[#00D4FF]/10 transition-colors disabled:opacity-50"
                data-testid="onboarding-gen-key">
                {generating ? 'Generating...' : 'Generate API Key'}
              </button>
            )}

            {apiKey && (
              <>
                <div className="p-3 mb-4 border border-[#F2A900]/30 bg-[#F2A900]/5">
                  <p className="text-[10px] text-[#F2A900] font-mono mb-1">Save this key — it won't be shown again:</p>
                  <code className="text-xs text-[#F5F1E8] font-mono break-all">{apiKey}</code>
                </div>

                {/* Platform tabs */}
                <div className="flex gap-1 mb-3">
                  {MCP_PLATFORMS.map(p => (
                    <button key={p.id} onClick={() => setPlatform(p.id)}
                      className={`flex-1 px-2 py-2 text-[10px] font-mono border transition-colors ${
                        platform === p.id ? 'border-[#FF4D00]/40 bg-[#FF4D00]/5 text-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0]'
                      }`}>{p.label}</button>
                  ))}
                </div>

                <div className="relative mb-4">
                  <pre className="text-xs font-mono p-3 bg-[#0A0A0A] border border-[#2A2A2A] whitespace-pre-wrap break-all">
                    {MCP_PLATFORMS.find(p => p.id === platform)?.cmd(apiKey)}
                  </pre>
                  <button onClick={() => copyText(MCP_PLATFORMS.find(p => p.id === platform)?.cmd(apiKey), 'config')}
                    className="absolute top-2 right-2 px-2 py-1 text-[10px] font-mono bg-[#2A2A2A] text-[#F5F1E8]">
                    {copied === 'config' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
              </>
            )}

            <div className="flex gap-3">
              <button onClick={() => setStep(1)}
                className="px-4 py-3 min-h-[44px] border border-[#2A2A2A] text-[#A0A0A0] text-sm font-mono hover:border-[#3A3A3A] transition-colors flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={() => setStep(3)}
                className="flex-1 py-3 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] font-bold font-display uppercase tracking-wider hover:bg-[#FF6B33] transition-colors flex items-center justify-center gap-2"
                data-testid="onboarding-next-2">
                {apiKey ? 'Continue' : 'Skip for Now'} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: First Fray */}
        {step === 3 && (
          <div data-testid="onboarding-step-3">
            <div className="text-center mb-6">
              <Zap className="w-8 h-8 mx-auto mb-3 text-[#FF4D00]" />
              <h2 className="text-xl font-bold font-display uppercase tracking-wider mb-2">Run Your First Fray</h2>
              <p className="text-sm text-[#A0A0A0]">
                We picked a playbook based on your role. One click to see AI models debate.
              </p>
            </div>

            <div className="p-4 mb-6 border border-[#FF4D00]/20 bg-[#FF4D00]/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FF4D00]/20 flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5 text-[#FF4D00]" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{ROLES.find(r => r.id === role)?.label || 'Strategy'} Review</p>
                  <p className="text-[10px] text-[#A0A0A0]">
                    5 AI models will debate from different perspectives. Takes ~2 minutes.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(2)}
                className="px-4 py-3 min-h-[44px] border border-[#2A2A2A] text-[#A0A0A0] text-sm font-mono hover:border-[#3A3A3A] transition-colors flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={launchFirstFray}
                className="flex-1 py-3 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] font-bold font-display uppercase tracking-wider hover:bg-[#FF6B33] transition-colors flex items-center justify-center gap-2"
                data-testid="launch-first-fray">
                <Zap className="w-4 h-4" /> Run Fray
              </button>
            </div>

            <button onClick={completeOnboarding}
              className="w-full mt-3 py-2 text-xs text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors font-mono"
              data-testid="skip-to-dashboard">
              Skip to Dashboard <ChevronRight className="w-3 h-3 inline" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
